<template>
  <v-avatar
    :size="avatarSize"
    class="user-avatar"
    :class="{
      'group-avatar': isGroupPost,
      'user-avatar--borderless': borderless,
    }"
  >
    <RetryableImg
      v-if="processedAvatar && !avatarError"
      :src="processedAvatar"
      :srcset="srcset"
      :alt="alt || 'User avatar'"
      cover
      @error="handleAvatarError"
    />
    <v-icon
      v-else
      icon="mdi-account-circle"
      :size="iconSize"
      color="surface-variant"
    />
  </v-avatar>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import RetryableImg from "@/components/common/RetryableImg.vue";
import { getAvatarThumbnailUrl } from "@/utils/imageUrl";
import type { ImageVariants } from "@/types";

interface Props {
  avatar?: string;
  avatarVariants?: ImageVariants | null;
  id?: string;
  alt?: string;
  size?: number | string;
  isGroupPost?: boolean;
  /** Prefer WebP format when available */
  preferWebp?: boolean;
  /** No outline ring (e.g. compact chips / quote PiP caption) */
  borderless?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  size: 40,
  isGroupPost: false,
  preferWebp: true,
  borderless: false,
});

const avatarError = ref(false);

const avatarSize = computed(() => {
  if (props.isGroupPost) return 20;
  return props.size;
});

const numericSize = computed(() => {
  return typeof avatarSize.value === "number"
    ? avatarSize.value
    : parseInt(avatarSize.value);
});

const iconSize = computed(() => {
  return Math.min(numericSize.value - 4, 24);
});

/**
 * Select the best variant URL based on display size.
 * - thumb (150px): for sizes <= 75px (2x density)
 * - small (500px): for sizes > 75px (2x density up to 250px)
 */
const bestVariantUrl = computed(() => {
  if (!props.avatarVariants) return null;

  const size = numericSize.value;
  const variants = props.avatarVariants;

  // For avatars, we only have thumb (150px) and small (500px)
  // Use thumb for sizes up to 75px (2x density), small for larger
  if (size <= 75) {
    return props.preferWebp && variants.thumbWebp
      ? variants.thumbWebp
      : variants.thumb;
  }

  return props.preferWebp && variants.smallWebp
    ? variants.smallWebp
    : variants.small;
});

/**
 * Generate srcset for responsive images.
 */
const srcset = computed(() => {
  if (!props.avatarVariants) return undefined;

  const variants = props.avatarVariants;
  const srcsetParts: string[] = [];

  if (props.preferWebp) {
    if (variants.thumbWebp) srcsetParts.push(`${variants.thumbWebp} 150w`);
    if (variants.smallWebp) srcsetParts.push(`${variants.smallWebp} 500w`);
  } else {
    if (variants.thumb) srcsetParts.push(`${variants.thumb} 150w`);
    if (variants.small) srcsetParts.push(`${variants.small} 500w`);
  }

  return srcsetParts.length > 0 ? srcsetParts.join(", ") : undefined;
});

const processedAvatar = computed(() => {
  // Prefer variant URL if available (API already gave us sized variants)
  if (bestVariantUrl.value) {
    return bestVariantUrl.value;
  }

  if (!props.avatar) {
    return null;
  }

  let url: string;
  // Handle m.closedcircuit.io URLs
  if (
    props.avatar.startsWith("https://m.closedcircuit.io/") ||
    props.avatar.startsWith("http://m.closedcircuit.io/")
  ) {
    url = props.avatar;
  } else if (
    props.avatar.startsWith("production/") ||
    props.avatar.startsWith("local/") ||
    props.avatar.startsWith("staging/")
  ) {
    url = `https://m.closedcircuit.io/${props.avatar}`;
  } else if (props.avatar.startsWith("http")) {
    url = props.avatar;
  } else {
    return props.avatar;
  }

  // Request thumbnail size from CDN (2x display size) to avoid 4–5s full-size download
  if (
    url.startsWith("https://m.closedcircuit.io/") ||
    url.startsWith("http://m.closedcircuit.io/")
  ) {
    return getAvatarThumbnailUrl(
      url,
      Math.min(96, numericSize.value * 2),
      props.avatarVariants,
    );
  }
  return url;
});

const handleAvatarError = () => {
  console.error("UserAvatar: Image failed to load:", processedAvatar.value);
  avatarError.value = true;
};
</script>

<style scoped lang="scss">
.user-avatar {
  border: 2px solid rgb(var(--v-theme-outline-variant));
  transition: border-color 0.2s ease;

  &:hover {
    border-color: rgb(var(--v-theme-primary));
  }
}

.group-avatar {
  border-width: 1px;
}

/* After .user-avatar / .group-avatar; compound selector + !important beats Vuetify .v-avatar ring */
.user-avatar.user-avatar--borderless {
  border: 0 solid transparent !important;
  outline: none !important;
  box-shadow: none !important;
  transition: none;

  &:hover {
    border-color: transparent !important;
    box-shadow: none !important;
  }
}

:deep(.v-img),
:deep(.v-img__img) {
  box-shadow: none !important;
}
</style>
